import { Database, KeyRound, Lock, ServerCog } from "lucide-react";
import { useEffect, useState } from "react";
import { getCatalogInstruments, getChartSettings, getProviderHealth, getTelegramSettings, saveChartSettings, saveTelegramSettings, sendTestAlert } from "../../data/api";
import { defaultChartSettings } from "../../data/chartSettings";
import { applyFontPreference, defaultFontStack, fontOptions, type FontOption } from "../../data/fonts";
import type { AssetClass, CatalogInstrumentRecord, ChartSettings, ProviderConfigRecord, ProviderHealthRecord, RiskLevel } from "../../types";

export default function SettingsView() {
  const [botToken, setBotToken] = useState("");
  const [chatId, setChatId] = useState("");
  const [status, setStatus] = useState("Not configured in this browser session");
  const [isSavingTelegram, setIsSavingTelegram] = useState(false);
  const [fontStatus, setFontStatus] = useState("Loading font preference...");
  const [chartSettings, setChartSettings] = useState<ChartSettings>(defaultChartSettings);
  const [selectedFont, setSelectedFont] = useState(defaultFontStack);
  const [providers, setProviders] = useState<ProviderConfigRecord[]>([]);
  const [health, setHealth] = useState<ProviderHealthRecord[]>([]);
  const [catalog, setCatalog] = useState<CatalogInstrumentRecord[]>([]);

  useEffect(() => {
    Promise.all([
      getTelegramSettings(),
      getProviderHealth().catch(() => ({ providers: [], health: [] })),
      getChartSettings(),
      getCatalogInstruments({ status: "candidate" }).catch(() => [])
    ])
      .then(([settings, providerPayload, savedChartSettings, catalogRows]) => {
        setStatus(settings.configured ? `Configured for ${settings.chatIdMasked}` : "Telegram is not configured yet.");
        setProviders(providerPayload.providers);
        setHealth(providerPayload.health);
        setCatalog(catalogRows);
        setChartSettings(savedChartSettings);
        setSelectedFont(savedChartSettings.ui.fontFamily);
        applyFontPreference(savedChartSettings.ui.fontFamily);
        setFontStatus("Font preference loaded");
      })
      .catch(() => setStatus("Unable to read Telegram status."));
  }, []);

  const save = async () => {
    if (isSavingTelegram) return;
    if (!botToken.trim() || !chatId.trim()) {
      setStatus("Enter both the bot token and chat ID before saving.");
      return;
    }

    setIsSavingTelegram(true);
    setStatus("Encrypting and saving server-side...");
    try {
      const saved = await saveTelegramSettings({ botToken, chatId });
      setBotToken("");
      setChatId("");
      setStatus(`Saved securely for ${saved.chatIdMasked}. Plaintext credentials were not returned to the frontend.`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Unable to save settings.");
    } finally {
      setIsSavingTelegram(false);
    }
  };

  const selectFont = async (font: FontOption) => {
    const nextSettings = {
      ...chartSettings,
      ui: {
        ...chartSettings.ui,
        fontFamily: font.stack
      }
    };
    setSelectedFont(font.stack);
    setChartSettings(nextSettings);
    applyFontPreference(font.stack);
    setFontStatus("Saving font preference...");

    try {
      const saved = await saveChartSettings(nextSettings);
      setChartSettings(saved);
      setSelectedFont(saved.ui.fontFamily);
      applyFontPreference(saved.ui.fontFamily);
      setFontStatus("Saved and applied globally");
    } catch (error) {
      setFontStatus(error instanceof Error ? error.message : "Unable to save font preference.");
    }
  };

  const test = async () => {
    setStatus("Sending Telegram test alert...");
    try {
      await sendTestAlert({
        severity: "medium",
        dedupeKey: `settings:test:${Date.now()}`,
        message: "[CP Dashboard] Settings test alert. Telegram relay and encrypted D1 settings are working."
      });
      setStatus("Telegram test alert sent.");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "Unable to send test alert.");
    }
  };

  return (
    <section className="route-page settings-page">
      <div className="page-head">
        <div>
          <p className="eyebrow">Settings</p>
          <h2>Data sources, Telegram relay, and provider risk controls</h2>
          <span>Secrets are submitted to the Worker and encrypted before being stored in D1. The frontend only receives masked status.</span>
        </div>
        <div className="status-pill warn">Best-effort crawl mode</div>
      </div>

      <div className="settings-grid-page">
        <section className="settings-panel">
          <div className="panel-section-title"><Lock /> Telegram Alert Configuration</div>
          <label className="field"><span>Bot Token</span><input type="password" value={botToken} onChange={(event) => setBotToken(event.target.value)} placeholder="123456:ABC..." autoComplete="off" spellCheck={false} /></label>
          <label className="field"><span>Chat ID</span><input type="password" value={chatId} onChange={(event) => setChatId(event.target.value)} placeholder="-1001234567890" autoComplete="off" spellCheck={false} /></label>
          <div className="settings-actions">
            <button className="btn primary" disabled={isSavingTelegram} onClick={() => void save()} type="button"><KeyRound /> {isSavingTelegram ? "Saving..." : "Save encrypted settings"}</button>
            <button className="btn" disabled={isSavingTelegram} onClick={() => void test()} type="button">Send test alert</button>
          </div>
          <div className="secure-note" aria-live="polite">{status}</div>
        </section>

        <section className="settings-panel">
          <div className="panel-section-title"><ServerCog /> Provider Configuration</div>
          {providers.length ? providers.map((provider) => {
            const providerHealth = health.find((item) => item.provider === provider.provider);
            return (
              <ProviderRow
                key={provider.provider}
                name={provider.provider}
                role={`${provider.providerTier} / ${provider.accessMode} / ${provider.enabled ? "enabled" : "disabled"}`}
                risk={provider.riskLevel}
                status={providerHealth?.status ?? "not checked"}
                note={`${providerHealth?.failureReason ?? provider.notes ?? "No provider note available."} License: ${provider.licenseStatus}.`}
              />
            );
          }) : (
            <>
              <ProviderRow name="Yahoo" role="Global indices / FX candidate" risk="high" status="fallback" note="Terms restrict automated collection without prior permission." />
              <ProviderRow name="Naver Finance" role="Korea index / FX fallback" risk="low" status="fallback" note="Use robots-allowed finance paths only." />
              <ProviderRow name="Investing.com" role="Global index fallback" risk="medium" status="fallback" note="Do not bypass restricted chart query pages." />
            </>
          )}
        </section>

        <section className="settings-panel wide">
          <div className="panel-section-title"><Database /> Future Market Data Catalog <span className="subhead-status">{catalog.length} inactive candidates</span></div>
          <div className="catalog-group-grid">
            {(["index", "futures", "fx", "commodity", "crypto", "rate", "volatility"] as AssetClass[]).map((assetClass) => (
              <CatalogGroup key={assetClass} assetClass={assetClass} instruments={catalog.filter((item) => item.assetClass === assetClass)} />
            ))}
          </div>
          <div className="secure-note">Candidate instruments remain read-only and generate no provider traffic until a mapping is verified and explicitly activated.</div>
        </section>

        <section className="settings-panel wide">
          <div className="panel-section-title">Font Type <span className="subhead-status">{fontStatus}</span></div>
          <div className="font-option-grid">
            {fontOptions.map((font) => (
              <button
                className={`font-option-card ${selectedFont === font.stack ? "is-active" : ""}`}
                key={font.id}
                onClick={() => void selectFont(font)}
                style={{ fontFamily: font.stack }}
                type="button"
              >
                <b>{font.label}</b>
                <span>{font.sample}</span>
              </button>
            ))}
          </div>
        </section>

        <section className="settings-panel wide">
          <div className="panel-section-title">Storage & Refresh Policy</div>
          <div className="policy-grid">
            <Policy title="Historical Data" body="Daily OHLC is stored in D1 through explicit backfill jobs and idempotent upserts." />
            <Policy title="Window Metrics" body="6M, 1Y, 3Y, 5Y, and 10Y metrics are precomputed after each successful refresh." />
            <Policy title="Refresh Cadence" body="The Cloudflare daily scheduler runs once at 22:30 UTC. All other refreshes require the manual Refresh Data command." />
            <Policy title="Auditability" body="Responses carry source, asOf, freshness, coverage, quality flags, and license status." />
          </div>
        </section>
      </div>
    </section>
  );
}

function ProviderRow({ name, role, risk, status, note }: { name: string; role: string; risk: RiskLevel; status: string; note: string }) {
  const className = risk === "high" ? "bad" : risk === "medium" ? "warn" : "";
  return (
    <div className="provider-row">
      <div><b>{name}</b><span>{role}</span></div>
      <em className={`status-pill ${className}`}>{risk}</em>
      <p><b className="provider-inline-status">{status}</b> {note}</p>
    </div>
  );
}

function Policy({ title, body }: { title: string; body: string }) {
  return <div className="policy-card"><b>{title}</b><span>{body}</span></div>;
}

function CatalogGroup({ assetClass, instruments }: { assetClass: AssetClass; instruments: CatalogInstrumentRecord[] }) {
  const providerNames = [...new Set(instruments.flatMap((instrument) => instrument.providers.map((provider) => provider.provider)))];
  return (
    <div className="catalog-group">
      <div className="catalog-group-head"><b>{assetClass === "fx" ? "FX" : assetClass}</b><span>{instruments.length}</span></div>
      <p>{instruments.slice(0, 6).map((instrument) => instrument.name).join(" / ") || "No candidates"}{instruments.length > 6 ? ` / +${instruments.length - 6} more` : ""}</p>
      <em>{providerNames.join(", ") || "Provider mapping pending"}</em>
    </div>
  );
}
